import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PreviewerDownloadIcon from "@/assets/summary/ic_previewer_download_mobile.svg?react";
import PreviewerCloseIcon from "@/assets/summary/ic_previewer_close_mobile.svg?react";

import { createPortal } from "react-dom";
import { debounce } from "@common/utils";
import { toast, Id } from "react-toastify";
import MobileSwipe from "../MobileSwipe";
import "./index.less";

let toastId: Id;
let messageTimer: NodeJS.Timeout;
const MessageTimeout = 3 * 1000;

interface MobileImagePreviewerProps { }
export interface MobileImagePreviewerRef {
  handlePreview: (images: string[], defaultIndex?: number) => void;
}

const MobileImagePreviewer = forwardRef<
  MobileImagePreviewerRef,
  MobileImagePreviewerProps
>((_props, ref) => {
  const [images, setImages] = useState<string[]>([]);
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const lastToastType = useRef<"first" | "last">();
  const activeIndexRef = useRef(activeIndex);
  const imagesLenRef = useRef(images.length);

  activeIndexRef.current = activeIndex;
  imagesLenRef.current = images.length;

  const closeToast = () => {
    toastId && toast.dismiss(toastId);
    toastId = "";
  };

  const setMessageTimer = (content: string, toastType: "first" | "last") => {
    if (!toastId || lastToastType.current !== toastType) {
      toastId && toast.dismiss(toastId);
      toastId = toast.dark(content, {
        autoClose: false,
      });
    }

    lastToastType.current = toastType;
    messageTimer && clearTimeout(messageTimer);
    messageTimer = setTimeout(() => {
      closeToast();
    }, MessageTimeout);
  };

  const handlePrev = debounce(() => {
    if (activeIndexRef.current > 0) {
      closeToast();
      setActiveIndex((prev) => prev - 1);
      activeIndexRef.current--;
    } else {
      setMessageTimer("当前已是第一张图片", "first");
    }
  }, 100);

  const handleNext = debounce(() => {
    if (activeIndexRef.current < imagesLenRef.current - 1) {
      closeToast();
      setActiveIndex((prev) => prev + 1);
      activeIndexRef.current++;
    } else {
      setMessageTimer("当前已是最后一张图", "last");
    }
  }, 100);

  const handleDownload = debounce(() => {
    const targetUrl = images[activeIndex];
    const link = document.createElement("a");
    link.href = targetUrl;
    link.download = "downloaded_image.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, 100);

  const handleClose = () => {
    setVisible(false);
  };

  const handlePreview = (images: string[], defaultIndex?: number) => {
    setImages(images);
    setActiveIndex(defaultIndex || 0);
    setVisible(true);
  };

  useImperativeHandle(ref, () => ({
    handlePreview,
    handleClose,
  }));

  const targetUrl = images[activeIndex];
  const root = document.getElementById("root");

  useEffect(() => {
    return () => {
      closeToast();
    };
  }, [visible]);

  return visible && root
    ? createPortal(
      <div
        className={`fixed top-0 left-0 w-full h-full`}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <MobileSwipe handlePrev={handlePrev} handleNext={handleNext}>
          <div className={`w-full h-full bg-black bg-opacity-70`}>
            <div className="absolute left-0 bg-#000 right-0 top-0 bottom-0">
              <img
                src={targetUrl}
                className="w-full h-full object-contain select-none no-touch-callout"
                draggable={false}
              />
            </div>
            <div className="px-3 w-full h-12 flex items-center justify-center text-mx-secondary mobile-page">
              <PreviewerDownloadIcon
                className="cursor-pointer no_drag"
                onClick={handleDownload}
              />
              <span className="w-fit m-auto text-#fff/30 font-500 text-4.5 lh-2.75 select-none">
                <span className="color-#fff">{activeIndex + 1}</span>/
                {images.length}
              </span>
              <PreviewerCloseIcon
                className="cursor-pointer no_drag"
                onClick={handleClose}
              />
            </div>
          </div>
        </MobileSwipe>
      </div>,
      root
    )
    : null;
});

export default MobileImagePreviewer;
